import React, { createContext, useContext, useEffect, useReducer, useState } from "react";

import api from "../../services/api";
import Mutex from "./Mutex";
import toastError from "../../errors/toastError";

//  **************
//  ** Contexts **
//  **************
const GlobalRegistersContext = createContext();

const useGlobalRegisters = () => useContext(GlobalRegistersContext);

//  ***************
//  ** Providers **
//  ***************
const GlobalRegistersProvider = ({ children }) => {
  //  ***************
  //  ** Variables **
  //  ***************

  // ***---- Flags ----***
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // ***---- Global Variables ----***
  const [allSettings, setAllSettings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allQuickAnswers, setAllQuickAnswers] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [allSubqueues, setAllSubqueues] = useState([]);
  const [allWhatsapps, setAllWhatsapps] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [userQueues, setUserQueues] = useState([]);
  const [queueOverrideAuthorizers, setQueueOverrideAuthorizers] = useState([]);

  const [contactViewContactId, setContactViewContactId] = useState(0);
  const [contactViewWhatsappId, setContactViewWhatsappId] = useState(0);

  const [importMessages, setImportMessages] = useState("disabled");

  const [userQueuesIdsMap, ] = useState(new Map());
  const [whatsappQueuesMap, ] = useState(new Map());
  const [whatsappPriorizationMap, ] = useState(new Map());

  // ***---- Mutexes ----***
  const mutexUserQueuesIds = Mutex.getInstancia("mutexUserQueuesIds");
  const mutexWhatsappQueues = Mutex.getInstancia("mutexWhatsappQueues");
  const mutexWhatsappPriorization = Mutex.getInstancia("mutexWhatsappPriorization");

  // ***---- Flags ----***
  const [areThereQueueOverrideRequests, setAreThereQueueOverrideRequests] = useState(false);
  const [shouldSyncRouteOnMenu, setShouldSyncRouteOnMenu] = useState(true);



  //  **************
  //  ** Reducers **
  //  **************
  const queueOverrideReducer = (state, action) => {
    if (action.type === "LOAD_QUEUE_OVERRIDES") {
      const queueOverrides = action.payload;
      const newQueueOverrides = [];

      queueOverrides.forEach(queueOverride => {
        const queueOverrideIndex = state.findIndex(qo => qo.id === queueOverride.id);
        if (queueOverrideIndex !== -1) state[queueOverrideIndex] = queueOverride;
        else newQueueOverrides.push(queueOverride);
      });

      return [...state, ...newQueueOverrides];
    }

    if (action.type === "UPDATE_QUEUE_OVERRIDE") {
      const queueOverride = action.payload;
      const queueOverrideIndex = state.findIndex(qo => qo.id === queueOverride.id);

      if (queueOverrideIndex !== -1) {
        state[queueOverrideIndex] = queueOverride;
        return [...state];
      }

      return [queueOverride, ...state];
    }

    if (action.type === "DELETE_QUEUE_OVERRIDE") {
      const queueOverrideId = action.payload;
      const queueOverrideIndex = state.findIndex(qo => qo.id === queueOverrideId);

      if (queueOverrideIndex !== -1) state.splice(queueOverrideIndex, 1);
      return [...state];
    }

    if (action.type === "RESET") {
      return [];
    }
  };

  const [queueOverrides, queueOverridesDispatch] = useReducer(queueOverrideReducer, []);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!isAuthorized) return;

    const fetchGlobalRegisters = async () => {
      try {
        const apiCalls = [
          api.get("/settings")
          , api.get("/AllUsers")
          , api.get("/allQuickAnswers")
          , api.get("/queue")
          , api.get("/subqueue")
          , api.get("/AllWhatsapps")
          , api.get("/category")
          , api.get("/label")
          , api.get("/whatsapp")
          , api.get("/userQueues")
          , api.get("/importMessages")
        ];

        const [
          SettingsResponse
          , AllUsersResponse
          , QuickAnswersResponse
          , QueuesResponse
          , SubqueuesResponse
          , AllWhatsappsResponse
          , CategoriesResponse
          , LabelsResponse
          , WhatsappsResponse
          , UserQueuesResponse
          , ImportMessagesResponse
        ] = await Promise.all(apiCalls);

        setAllSettings(SettingsResponse.data);
        setAllUsers(AllUsersResponse.data.users);
        setAllQuickAnswers(QuickAnswersResponse.data.quickAnswers);
        setAllQueues(QueuesResponse.data);
        setAllSubqueues(SubqueuesResponse.data);
        setAllWhatsapps(AllWhatsappsResponse.data);
        setAllCategories(CategoriesResponse.data);
        setAllLabels(LabelsResponse.data);
        setWhatsapps(WhatsappsResponse.data);
        setUserQueues(UserQueuesResponse.data);
        setImportMessages(ImportMessagesResponse.data.importMessages);

        setQueueOverrideAuthorizers(AllUsersResponse.data.users
          .filter(user => user.canAuthorizeQueueOverride)
          .map(user => ({
            id: user.id
            , name: user.name
            , isOwner: user.isOwner
            , profile: user.profile
            , forceAcceptFirstTicket: user.forceAcceptFirstTicket
            , canAuthorizeQueueOverride: user.canAuthorizeQueueOverride
          }))
        );

        setIsLoading(false);
      }
      catch (exception) {
        setIsLoading(false);
        console.log("- Global Registers - Use Effect Exception:", exception);
        toastError(exception);
      }
    }

    fetchGlobalRegisters();    
  }, [isAuthorized]);



  //  ***************
  //  ** Functions **
  //  ***************

  // ***---- Getters ----***
  const getSetting = (key) => {  
    const { value } = allSettings.find(setting => setting.key === key);
    return value;
	};

  const getUserQueuesIds = async (userId) => {
    if (!userId) return [];

    await mutexUserQueuesIds.lock();

    try {
      const queues = userQueuesIdsMap.get(userId);  
      if (queues) return queues;
  
      const { data } = await api.get(`/userQueuesIds/${userId}`);
      userQueuesIdsMap.set(userId, data);
      return data;
    }
    finally {
      await mutexUserQueuesIds.unlock();
    }
  };

  const getWhatsappQueues = async (whatsappId) => {
    if (!whatsappId) return [];
    
    await mutexWhatsappQueues.lock();

    try {
      const queues = whatsappQueuesMap.get(whatsappId);  
      if (queues) return queues;
  
      const { data } = await api.get(`/whatsappQueues/${whatsappId}`);
      whatsappQueuesMap.set(whatsappId, data);
      return data;
    }
    finally {
      await mutexWhatsappQueues.unlock();
    }
  };

  const getWhatsappPriorization = async (userId) => {
    if (!userId) return [];

    await mutexWhatsappPriorization.lock();

    try {
      const priorizations = whatsappPriorizationMap.get(userId);
      if (priorizations) return priorizations;

      const { data } = await api.get(`/whatsappPriorization/${userId}`);
      whatsappPriorizationMap.set(userId, data);
      return data;
    }
    finally {
      await mutexWhatsappPriorization.unlock();
    }
  };

  // ***---- Updaters ----***
  const updateAllUsers = async () => {
    const apiCalls = [api.get("/AllUsers"), api.get("/userQueues")];
    const [AllUsersResponse, UserQueuesResponse] = await Promise.all(apiCalls);

    userQueuesIdsMap.clear();
    whatsappPriorizationMap.clear();
    setAllUsers(AllUsersResponse.data.users);
    setUserQueues(UserQueuesResponse.data);

    setQueueOverrideAuthorizers(AllUsersResponse.data.users
      .filter(user => user.canAuthorizeQueueOverride)
      .map(user => ({
        id: user.id
        , name: user.name
        , isOwner: user.isOwner
        , profile: user.profile
        , forceAcceptFirstTicket: user.forceAcceptFirstTicket
        , canAuthorizeQueueOverride: user.canAuthorizeQueueOverride
      }))
    );
  };

  const updateAllQuickAnswers = async () => {
    const { data } = await api.get("/allQuickAnswers");
    setAllQuickAnswers(data.quickAnswers);
  };

  const updateAllQueues = async () => {
    const { data } = await api.get("/queue");
    setAllQueues(data);
  };

  const updateAllSubqueues = async () => {
    const { data } = await api.get("/subqueue");
    setAllSubqueues(data);
  };

  const updateAllCategories = async () => {
    const { data } = await api.get("/category");
    setAllCategories(data);
  };

  const updateAllLabels = async () => {
    const { data } = await api.get("/label");
    setAllLabels(data);
  };

  const updateAllWhatsapps = async () => {
    const apiCalls = [api.get("/AllWhatsapps"), api.get("/whatsapp")];
    const [AllWhatsappsResponse, WhatsappResponse] = await Promise.all(apiCalls);

    whatsappQueuesMap.clear();
    setAllWhatsapps(AllWhatsappsResponse.data);
    setWhatsapps(WhatsappResponse.data);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <GlobalRegistersContext.Provider
      value={{
        isLoading, allUsers, allQuickAnswers,
        allQueues, allSubqueues, allWhatsapps,
        allCategories, allLabels, whatsapps,
        userQueues, importMessages, queueOverrideAuthorizers,
        areThereQueueOverrideRequests, shouldSyncRouteOnMenu,
        
        setAreThereQueueOverrideRequests, setShouldSyncRouteOnMenu,
        setIsAuthorized, setAllSettings,

        getSetting, getUserQueuesIds, getWhatsappQueues,
        getWhatsappPriorization,

        updateAllUsers, updateAllQuickAnswers, updateAllQueues,
        updateAllSubqueues, updateAllCategories, updateAllLabels,
        updateAllWhatsapps,

        contactViewContactId, setContactViewContactId,
        contactViewWhatsappId, setContactViewWhatsappId,

        // ***---- Reducers ----***
        queueOverrideReducer, queueOverrides, queueOverridesDispatch
      }}
    >
      {children}
    </GlobalRegistersContext.Provider>
  );
};

export { useGlobalRegisters, GlobalRegistersProvider };
